/* ZSportsTech — variant-select.js
   Shared variant-selection engine: swatches, option pills, progressive
   availability, and variant resolution. Extracted from
   section-pdp-accessory.js so the accessory PDP and the variant-picker
   modal never drift apart — they run the exact same code.

   Public API: window.SCVariantSelect.{swatchStyle, isColourOption, create}

   NORMALISED product shape expected by create(container, product, onChange):
     {
       optionNames: ['Grip Type','Colour','Pack'],
       variants: [ { id: 123, opts: ['Z-Bat Grip','Black/Red','Pack of 1'],
                     price: '₹200', available: true, img: 'https://...' or null }, ... ]
     }

   The engine knows nothing about specific element IDs — it renders into
   whatever container element it is given and reports the resolved variant
   via onChange(variant) (variant may be null when no combination resolves).
   ─────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var SWATCH = {
    'white': '#FFFFFF',
    'black': '#1A1A1A',
    'blue': '#1F4FA8',
    'green': '#2E7D4F',
    'red': '#C0362C',
    'pink': '#E86A9A',
    'orange': '#E8792B',
    'gold': '#D4AF37'
  };

  function swatchStyle(value) {
    var v = String(value).toLowerCase().trim();
    if (v.indexOf('mix') > -1) {
      return 'background: conic-gradient(#C0362C, #E8792B, #D4AF37, #2E7D4F, #1F4FA8, #E86A9A, #C0362C);';
    }
    var parts = v.split(/[\/&+]|\s+with\s+/).map(function (s) { return s.trim(); }).filter(Boolean);
    var cols = parts.map(function (p) {
      for (var k in SWATCH) { if (p.indexOf(k) > -1) return SWATCH[k]; }
      return null;
    }).filter(Boolean);
    if (cols.length >= 2) {
      return 'background: linear-gradient(135deg, ' + cols[0] + ' 0 50%, ' + cols[1] + ' 50% 100%);';
    }
    if (cols.length === 1) return 'background: ' + cols[0] + ';';
    return 'background: var(--offwhite);';
  }

  function knownColour(value) {
    var v = String(value).toLowerCase();
    if (v.indexOf('mix') > -1) return true;
    return Object.keys(SWATCH).some(function (k) { return v.indexOf(k) > -1; });
  }

  /* Swatches only when the option is a colour AND every value is a real colour.
     Mixed options ("Colour or Size" -> Black, Blue, Kids, Men's) fall back to
     pills, so a size never renders as a blank circle. */
  function isColourOption(name, values) {
    return /colour|color/i.test(name) && values.every(knownColour);
  }

  function create(container, product, onChange) {
    var NAMES = (product && product.optionNames) || [];
    var VARIANTS = (product && product.variants) || [];
    var destroyed = false;

    function currentVariant() {
      if (!VARIANTS.length) return null;
      return VARIANTS.filter(function (v) {
        return NAMES.every(function (_, i) { return v.opts[i] === state[i]; });
      })[0] || null;
    }

    function notify() {
      if (destroyed) return;
      if (typeof onChange === 'function') onChange(currentVariant());
    }

    if (!VARIANTS.length) {
      if (container) container.innerHTML = '';
      notify();
      return { getVariant: currentVariant, destroy: function () { destroyed = true; } };
    }

    var state = NAMES.map(function (_, i) {
      var first = VARIANTS.filter(function (v) { return v.available; })[0] || VARIANTS[0];
      return first.opts[i];
    });

    /* The customer's own last explicit pick at each level, independent of
       whatever repair() had to temporarily fall back to. Restored in
       preference to "just grab the first reachable value" once it becomes
       reachable again — e.g. Colour=Black/Red survives a detour through a
       Grip Type that doesn't offer it, instead of being silently replaced
       by whatever repair() picked in the meantime. */
    var desired = state.slice();

    function valuesFor(i) {
      var seen = [];
      VARIANTS.forEach(function (v) {
        if (seen.indexOf(v.opts[i]) === -1 && v.opts[i]) seen.push(v.opts[i]);
      });
      return seen;
    }

    /* A value at level i is reachable when some variant matches every EARLIER
       choice and is in stock. Later levels stay open so a colour never looks
       dead just because the currently-picked size is gone. */
    function reachable(i, value) {
      return VARIANTS.some(function (v) {
        if (!v.available) return false;
        if (v.opts[i] !== value) return false;
        for (var k = 0; k < i; k++) { if (v.opts[k] !== state[k]) return false; }
        return true;
      });
    }

    /* After a change, walk forward and repair any now-impossible later choice. */
    function repair() {
      for (var i = 0; i < NAMES.length; i++) {
        /* Prefer restoring what the customer actually picked, not just
           whatever state[i] currently holds — a value can remain reachable
           (e.g. "White" exists under both Octo Grip and Z-Bat Grip) even
           after it's no longer what they chose, so checking reachability
           of state[i] alone would never bring "Black/Red" back once the
           detour through Octo Grip forced a switch to White. */
        if (reachable(i, desired[i])) {
          state[i] = desired[i];
        } else if (!reachable(i, state[i])) {
          var ok = valuesFor(i).filter(function (val) { return reachable(i, val); });
          if (ok.length) state[i] = ok[0];
        }
      }
    }

    function renderOptions() {
      if (!container) return;
      container.innerHTML = '';
      NAMES.forEach(function (name, i) {
        var vals = valuesFor(i);
        if (vals.length <= 1 && NAMES.length === 1 && vals[0] === 'Default Title') return;

        var wrap = document.createElement('div');
        wrap.className = 'sc-apdp__opt';

        var lbl = document.createElement('div');
        lbl.className = 'sc-apdp__lbl';
        lbl.innerHTML = name + ' <span class="sel">' + (state[i] || '') + '</span>';
        wrap.appendChild(lbl);

        var box = document.createElement('div');
        box.className = 'sc-apdp__vals';
        box.setAttribute('role', 'radiogroup');
        box.setAttribute('aria-label', name);

        var colour = isColourOption(name, vals);
        vals.forEach(function (val) {
          var b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('role', 'radio');
          b.setAttribute('aria-checked', String(state[i] === val));
          var can = reachable(i, val);
          if (!can) b.setAttribute('aria-disabled', 'true');

          if (colour) {
            b.className = 'sc-apdp__sw';
            b.title = val;
            b.setAttribute('aria-label', val);
            var chip = document.createElement('i');
            chip.setAttribute('style', swatchStyle(val));
            b.appendChild(chip);
          } else {
            b.className = 'sc-apdp__pill';
            b.textContent = val;
          }

          b.addEventListener('click', function () {
            if (!can) return;
            state[i] = val;
            desired[i] = val;
            repair();
            render();
          });
          box.appendChild(b);
        });

        wrap.appendChild(box);
        container.appendChild(wrap);
      });
    }

    function render() {
      renderOptions();
      notify();
    }

    function destroy() {
      destroyed = true;
      if (container) container.innerHTML = '';
    }

    repair();
    render();

    return { getVariant: currentVariant, destroy: destroy };
  }

  window.SCVariantSelect = {
    swatchStyle: swatchStyle,
    isColourOption: isColourOption,
    create: create
  };
}());
